import { EFinancingProgram, EProvince, getEProvinceList } from '@if/core/types'
import { useCreateCreditApplication } from '@src/data/api/credit-application/credit-application-api'
import { useFinancingConfig } from '@src/data/selectors/app-config-selectors'
import { useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Breadcrumb, PageSpinner } from '../../components'
import { buildDraftCreditApplicationSchema, CreditApplication } from '../../data/types'
import { reportErrorToServer } from '../../services/error-logger'
import CreditApplicationForm from './components/CreditApplicationForm/CreditApplicationForm'

const CreateCreditApplicationPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [createCreditApplication, isCreating] = useCreateCreditApplication()
  const navigate = useNavigate()
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const financingConfig = useFinancingConfig(financingProgramId)
  const [searchParams] = useSearchParams()

  const [creditApplication] = useState<CreditApplication>(() => {
    const base = buildDraftCreditApplicationSchema({ financingConfig }).getDefault() as unknown as CreditApplication
    const merchantIdParam = searchParams.get('merchantId')
    const stateIsoParam = searchParams.get('stateIso')
    const requestedAmountParam = searchParams.get('requestedAmount')

    const stateIso =
      stateIsoParam && getEProvinceList().includes(stateIsoParam as EProvince) ? (stateIsoParam as EProvince) : null
    const requestedAmount =
      requestedAmountParam != null && requestedAmountParam !== '' && !Number.isNaN(Number(requestedAmountParam))
        ? Number(requestedAmountParam)
        : null

    return {
      ...base,
      financingProgramId: financingProgramId!,
      ...(merchantIdParam ? { merchantId: merchantIdParam } : {}),
      ...(requestedAmount != null
        ? { requestedLoanAmount: requestedAmount, initialRequestedLoanAmount: requestedAmount }
        : {}),
      applicant: {
        ...base.applicant,
        currentAddress: {
          ...base.applicant.currentAddress,
          ...(stateIso ? { stateIso } : {}),
        },
      },
    }
  })
  document.title = t('breadcrumbs.newApplication')
  const onSave = (data: Partial<CreditApplication>) => {
    createCreditApplication(data)
      .then((creditApp) => {
        navigate(`/Applications/${creditApp.financingProgramId}/${creditApp.id}/view`, { replace: true })
      })
      .catch(reportErrorToServer)
  }

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    { path: '#', label: t('breadcrumbs.newApplication') },
  ]

  return (
    <div>
      <PageSpinner isLoading={isCreating} withBackdrop />
      <Breadcrumb trees={breadCrumbs} />
      <CreditApplicationForm creditApplication={creditApplication} onSave={onSave} />
    </div>
  )
}

export default CreateCreditApplicationPage
